import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalKey = 'privacy' | 'terms' | null;
const content = {
  privacy: {
    title: 'Política de Privacidad',
    body: ['En Protocolo 72 Horas valoramos profundamente tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.', 'Información que recopilamos: Únicamente recopilamos los datos que tú nos proporcionas voluntariamente, como tu nombre y correo electrónico al adquirir la guía o suscribirte a nuestros recursos.', 'Uso de la información: Utilizamos tus datos exclusivamente para entregarte el producto adquirido, enviarte contenido relacionado con tu compra y mejorar tu experiencia. Nunca compartimos ni vendemos tu información a terceros.', 'Seguridad: Implementamos medidas técnicas y organizativas adecuadas para proteger tus datos contra accesos no autorizados, pérdida o uso indebido.', 'Tus derechos: Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, puedes solicitarlo a través de los canales disponibles en tu cuenta.', 'Cambios en esta política: Nos reservamos el derecho de actualizar esta política. Cualquier cambio será notificado en esta misma página.']
  },
  terms: {
    title: 'Términos y Condiciones',
    body: ['Bienvenida/o a Protocolo 72 Horas. Al acceder y utilizar nuestra guía digital, aceptas los siguientes términos y condiciones de uso.', 'Uso del contenido: Todo el material incluido en "El Protocolo 72 Horas" es de carácter educativo y personal. No está permitida la reproducción, distribución o reventa del contenido sin autorización expresa por escrito.', 'Naturaleza del producto: Esta guía ofrece estrategias basadas en psicología relacional. No sustituye terapia profesional, asesoramiento médico, psicológico ni legal. Cada situación es única y los resultados pueden variar.', 'Garantía de satisfacción: Ofrecemos una garantía de devolución de 30 días desde la fecha de compra. Si el contenido no cumple con tus expectativas, podrás solicitar el reembolso completo sin necesidad de justificación.', 'Limitación de responsabilidad: Protocolo 72 Horas no se hace responsable de las decisiones personales tomadas a partir de la lectura de esta guía. El uso del contenido es bajo tu propia responsabilidad.', 'Propiedad intelectual: Todos los derechos sobre el contenido, marca y materiales asociados son propiedad de Protocolo 72 Horas y están protegidos por las leyes de propiedad intelectual.', 'Modificaciones: Nos reservamos el derecho a modificar estos términos en cualquier momento. Las versiones actualizadas estarán disponibles en esta misma sección.']
  }
};
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalKey>(null);
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  return <>
      <footer className="bg-cream border-t border-navy/10 py-12 px-6" data-id="element-50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-51">
          <div className="font-serif font-semibold text-lg text-navy" data-id="element-52">
            Protocolo 72H
          </div>

          <div className="flex items-center gap-6 text-sm text-charcoal-light" data-id="element-53">
            <button onClick={() => setOpenModal('privacy')} className="hover:text-navy transition-colors" data-id="element-54">
              Privacidad
            </button>
            <button onClick={() => setOpenModal('terms')} className="hover:text-navy transition-colors" data-id="element-55">
              Términos
            </button>
          </div>

          <div className="text-sm text-charcoal-light/60" data-id="element-56">
            © {new Date().getFullYear()} Protocolo 72 Horas. Todos los derechos
            reservados.
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-57">
        {openModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-navy/60 backdrop-blur-sm" onClick={() => setOpenModal(null)} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-58">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative bg-cream max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-59">
              <div className="sticky top-0 bg-cream border-b border-navy/10 px-8 py-5 flex items-center justify-between rounded-t-2xl" data-id="element-60">
                <h3 id="modal-title" className="font-serif text-2xl text-navy" data-id="element-61">
                  {content[openModal].title}
                </h3>
                <button onClick={() => setOpenModal(null)} className="text-charcoal-light hover:text-navy transition-colors p-1 -mr-1" aria-label="Cerrar" data-id="element-62">
                  <X className="w-5 h-5" data-id="element-63" />
                </button>
              </div>

              <div className="px-8 py-6 space-y-4" data-id="element-64">
                {content[openModal].body.map((paragraph, i) => <p key={i} className="text-charcoal-light leading-relaxed text-[15px]" data-id="element-65">
                    {paragraph}
                  </p>)}
              </div>

              <div className="px-8 pb-6" data-id="element-66">
                <button onClick={() => setOpenModal(null)} className="w-full bg-navy text-cream py-3 rounded-sm font-medium hover:bg-navy-light transition-colors duration-300" data-id="element-67">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}